import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import CommonHeadingNBtns from "../../common-components/CommonHeadingNBtns";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";
import { useGetFetchNew } from "../../../data/Queries";
import {
  add_academy_course_schedule,
  update_academy_course_schedule,
  top_courses,
} from "../../../data/ApiEndpoints";
// import "./schedule.css";
import { scheduleSchema } from "../../../schema/courseSchema";
import {
  convertTo12HourFormat,
  convertTo24HourFormat1,
} from "../../common-data/Converts";
import { fetchInHouseUsersData } from "../../../redux/reducers/dropdownApiSlice";
import "../../../styles/website/management-forms.css";

const ManageSchedule = ({
  closeModal,
  token,
  initialValues,
  refetchScheduleList,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(!!initialValues);
  const { inhouseUsers: trainerData, loading: trainersLoading } = useSelector(
    (state) => state.dropdown
  );

  const {
    data: coursesData,
    isLoading: coursesLoading,
    refetch,
  } = useGetFetchNew({
    token,
    key: "academy_courses",
    url: top_courses,
  });

  useEffect(() => {
    dispatch(
      fetchInHouseUsersData({
        token,
        qps: `user_types=trainer,admin`,
        pageNo: 1,
        limit: 100,
      })
    );
  }, []);

  const formik = useFormik({
    initialValues: initialValues || {
      course_id: "",
      start_date: "",
      end_date: "",
      batch_type: "",
      batch_start_time: "",
      batch_end_time: "",
      location: "",
      instructor: "",
    },
    validationSchema: scheduleSchema,
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        const method = isEdit ? "PUT" : "POST";
        const endpoint = isEdit
          ? update_academy_course_schedule
          : add_academy_course_schedule;

        // Convert times to 24-hour format before sending to API
        const payload = {
          ...values,
          batch_start_time: convertTo24HourFormat1(values.batch_start_time),
          batch_end_time: convertTo24HourFormat1(values.batch_end_time),
        };

        if (isEdit) {
          payload.uid = initialValues?.uid;
        }

        const result = await dispatch(
          commonPostPutApi({ method, endpoint, payload, token })
        );

        if (result?.payload?.status) {
          toast.success(result?.payload?.message || "Success!");
          formik.resetForm();
          refetchScheduleList();
          if (isEdit) {
            setIsEdit(false);
          }
          closeModal();
        } else {
          const errorMessage =
            result?.error?.message || result?.payload?.message;
          toast.error(errorMessage || "Something Went Wrong!");
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Something went wrong!");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleTimeChange = (e) => {
    const { name, value } = e.target;
    formik.setFieldValue(name, value);
  };

  // Helper function to format shift time if available
  const formatInstructorLabel = (instructor) => {
    if (instructor.label) {
      return instructor.label;
    }

    let label = instructor.info?.name || "Unknown";

    // Add shift details if available
    if (
      instructor.info?.shift_details &&
      instructor.info.shift_details.length > 0
    ) {
      const shift = instructor.info.shift_details[0];
      if (shift.start_time && shift.end_time) {
        label += ` (${convertTo12HourFormat(
          shift.start_time
        )}-${convertTo12HourFormat(shift.end_time)})`;
      }
    }

    // Add gym name if available
    if (instructor.info?.gym_name) {
      label += ` - ${instructor.info.gym_name}`;
    }

    return label;
  };

  return (
    <div className="createNewCTOModalContainer mod-width-50p">
      <CommonHeadingNBtns
        title={isEdit ? "Update Course Schedule" : "Add Course Schedule"}
        onClose={closeModal}
        btnTxt={isLoading ? "Processing..." : isEdit ? "Update " : "Add"}
        onClick={formik.handleSubmit}
        isDisabled={isLoading}
      />

      <form
        className="management-form-container"
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit();
        }}
      >
        <div className="management-form-elements">
          {/* Course and Location in one row */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "5px" }}>
            {/* Course Selection */}
            <div className="management-form-group" style={{ flex: 1 }}>
              <label htmlFor="course_id" className="management-form-label">
                Course <span className="required-indicator">*</span>
              </label>
              <select
                id="course_id"
                name="course_id"
                value={formik.values.course_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`management-form-input custom-select ${
                  formik.touched.course_id && formik.errors.course_id
                    ? "error"
                    : ""
                }`}
                disabled={coursesLoading}
              >
                <option value="">Select Course</option>
                {Array.isArray(coursesData?.data) &&
                  coursesData?.data?.map((course) => (
                    <option key={course.uid} value={course.uid}>
                      {course.title}
                    </option>
                  ))}
              </select>
              {coursesLoading && (
                <div className="management-form-error">Loading courses...</div>
              )}
              {formik.touched.course_id && formik.errors.course_id && (
                <div className="management-form-error">
                  {formik.errors.course_id}
                </div>
              )}
            </div>

            {/* Location */}
            <div className="management-form-group" style={{ flex: 1 }}>
              <label htmlFor="location" className="management-form-label">
                Location <span className="required-indicator">*</span>
              </label>
              <input
                id="location"
                type="text"
                name="location"
                value={formik.values.location}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter Location"
                className={`management-form-input ${
                  formik.touched.location && formik.errors.location
                    ? "error"
                    : ""
                }`}
              />
              {formik.touched.location && formik.errors.location && (
                <div className="management-form-error">
                  {formik.errors.location}
                </div>
              )}
            </div>
          </div>

          {/* Start Date and End Date in one row */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "5px" }}>
            {/* Start Date */}
            <div className="management-form-group" style={{ flex: 1 }}>
              <label htmlFor="start_date" className="management-form-label">
                Start Date <span className="required-indicator">*</span>
              </label>
              <input
                id="start_date"
                type="date"
                name="start_date"
                value={formik.values.start_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`management-form-input custom-date-input ${
                  formik.touched.start_date && formik.errors.start_date
                    ? "error"
                    : ""
                }`}
                style={{
                  paddingRight: "5px",
                  cursor: "pointer",
                  caretColor: "#212529",
                  "-webkit-text-fill-color": "#212529",
                }}
              />
              {formik.touched.start_date && formik.errors.start_date && (
                <div className="management-form-error">
                  {formik.errors.start_date}
                </div>
              )}
            </div>

            {/* End Date */}
            <div className="management-form-group" style={{ flex: 1 }}>
              <label htmlFor="end_date" className="management-form-label">
                End Date <span className="required-indicator">*</span>
              </label>
              <input
                id="end_date"
                type="date"
                name="end_date"
                value={formik.values.end_date}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={`management-form-input custom-date-input ${
                  formik.touched.end_date && formik.errors.end_date
                    ? "error"
                    : ""
                }`}
                style={{
                  paddingRight: "5px",
                  cursor: "pointer",
                  caretColor: "#212529",
                  "-webkit-text-fill-color": "#212529",
                }}
              />
              {formik.touched.end_date && formik.errors.end_date && (
                <div className="management-form-error">
                  {formik.errors.end_date}
                </div>
              )}
            </div>
          </div>

          {/* Batch Type */}
          <div className="management-form-group">
            <label htmlFor="batch_type" className="management-form-label">
              Batch Type <span className="required-indicator">*</span>
            </label>
            <select
              id="batch_type"
              name="batch_type"
              value={formik.values.batch_type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`management-form-input custom-select ${
                formik.touched.batch_type && formik.errors.batch_type
                  ? "error"
                  : ""
              }`}
            >
              <option value="">Select Batch Type</option>
              <option value="weekend">Weekend</option>
              <option value="weekday">Weekday</option>
            </select>
            {formik.touched.batch_type && formik.errors.batch_type && (
              <div className="management-form-error">
                {formik.errors.batch_type}
              </div>
            )}
          </div>

          {/* Batch Times */}
          <div style={{ display: "flex", gap: "16px", marginBottom: "5px" }}>
            <div className="management-form-group" style={{ flex: 1 }}>
              <label
                htmlFor="batch_start_time"
                className="management-form-label"
              >
                Start Time <span className="required-indicator">*</span>
              </label>
              <input
                id="batch_start_time"
                type="time"
                name="batch_start_time"
                value={formik.values.batch_start_time}
                onChange={handleTimeChange}
                onBlur={formik.handleBlur}
                className={`management-form-input ${
                  formik.touched.batch_start_time &&
                  formik.errors.batch_start_time
                    ? "error"
                    : ""
                }`}
              />
              {formik.touched.batch_start_time &&
                formik.errors.batch_start_time && (
                  <div className="management-form-error">
                    {formik.errors.batch_start_time}
                  </div>
                )}
            </div>
            <div className="management-form-group" style={{ flex: 1 }}>
              <label htmlFor="batch_end_time" className="management-form-label">
                End Time <span className="required-indicator">*</span>
              </label>
              <input
                id="batch_end_time"
                type="time"
                name="batch_end_time"
                value={formik.values.batch_end_time}
                onChange={handleTimeChange}
                onBlur={formik.handleBlur}
                className={`management-form-input ${
                  formik.touched.batch_end_time && formik.errors.batch_end_time
                    ? "error"
                    : ""
                }`}
              />
              {formik.touched.batch_end_time &&
                formik.errors.batch_end_time && (
                  <div className="management-form-error">
                    {formik.errors.batch_end_time}
                  </div>
                )}
            </div>
          </div>

          {/* Instructor Dropdown */}
          <div className="management-form-group">
            <label htmlFor="instructor" className="management-form-label">
              Instructor <span className="required-indicator">*</span>
            </label>
            <select
              id="instructor"
              name="instructor"
              value={formik.values.instructor}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`management-form-input custom-select ${
                formik.touched.instructor && formik.errors.instructor
                  ? "error"
                  : ""
              }`}
              disabled={trainersLoading}
            >
              <option value="">Select Instructor</option>
              {Array.isArray(trainerData) &&
                trainerData.map((instructor) => (
                  <option key={instructor.value} value={instructor.value}>
                    {instructor.label || formatInstructorLabel(instructor)}
                  </option>
                ))}
            </select>
            {trainersLoading && (
              <div className="management-form-error">
                Loading instructors...
              </div>
            )}
            {formik.touched.instructor && formik.errors.instructor && (
              <div className="management-form-error">
                {formik.errors.instructor}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default ManageSchedule;
